import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { PageMeta } from '@/components/seo/PageMeta'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DataSourceList,
  CreateDataSourceDialog,
  DataSourceFieldsPanel,
} from '@/components/data-sources'
import { dataSourceService } from '@/lib/api/services/dataSources'
import type {
  DataSource,
  DataSourceField,
  CreateDataSourceRequest,
  UpdateDataSourceRequest,
} from '@/lib/api/types'
import {
  Plus,
  RefreshCw,
  Database,
  Loader2,
  AlertCircle,
  X,
  Building2,
  ShieldAlert,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/lib/contexts/auth-context'

interface TenantOption {
  id: string
  name: string
}

export default function DataSourcesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tenantId = searchParams.get('tenantId')
  const [tenantName, setTenantName] = useState<string | null>(null)
  const [tenants, setTenants] = useState<TenantOption[]>([])
  const { user: currentUser, hasPermission, hasAnyRole, isLoading: authLoading } = useAuth()

  // Check if user has admin access
  const canAccessPage = currentUser?.isSuperAdmin || hasAnyRole(['Admin', 'Tenant Admin']) || hasPermission('datasource.view')

  const [dataSources, setDataSources] = useState<DataSource[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDataSource, setEditDataSource] = useState<DataSource | null>(null)

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show access denied if user doesn't have permission
  if (!canAccessPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <ShieldAlert className="h-12 w-12 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground max-w-md mb-6">
          You don't have permission to access the Data Sources page. Please contact your administrator if you need access.
        </p>
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    )
  }
  const [deleteDataSource, setDeleteDataSource] = useState<DataSource | null>(null)
  const [viewFieldsDataSource, setViewFieldsDataSource] = useState<DataSource | null>(null)
  const [fields, setFields] = useState<DataSourceField[]>([])
  const [fieldsLoading, setFieldsLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    cacheDurationMinutes: 60,
    isEnabled: true,
  })

  // Load tenants for filter dropdown
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const { tenantsService } = await import('@/lib/api/services/tenants')
        const response = await tenantsService.getTenants({ pageSize: 100, sortBy: 'name' })
        setTenants(response.items?.map((t: { id: string; name: string }) => ({ id: t.id, name: t.name })) || [])
      } catch {
        // Ignore error
      }
    }
    loadTenants()
  }, [])

  // Load tenant name when filtering by tenant
  useEffect(() => {
    const loadTenantName = async () => {
      if (tenantId) {
        try {
          const { tenantsService } = await import('@/lib/api/services/tenants')
          const tenant = await tenantsService.getTenant(tenantId)
          setTenantName(tenant.name)
        } catch {
          // Ignore error
        }
      }
    }
    loadTenantName()
  }, [tenantId])

  // Clear tenant filter
  const clearTenantFilter = () => {
    searchParams.delete('tenantId')
    setSearchParams(searchParams)
    setTenantName(null)
  }

  // Set tenant filter
  const setTenantFilter = (id: string) => {
    if (id === 'all') {
      clearTenantFilter()
    } else {
      searchParams.set('tenantId', id)
      setSearchParams(searchParams)
    }
  }

  const loadDataSources = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await dataSourceService.getDataSources(tenantId || undefined)
      setDataSources(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data sources')
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    loadDataSources()
  }, [loadDataSources])

  // Load fields when viewing a data source
  useEffect(() => {
    if (viewFieldsDataSource) {
      setFieldsLoading(true)
      dataSourceService
        .getDataSourceFields(viewFieldsDataSource.id)
        .then(setFields)
        .catch((err) => console.error('Failed to load fields:', err))
        .finally(() => setFieldsLoading(false))
    } else {
      setFields([])
    }
  }, [viewFieldsDataSource])

  // Initialize edit form when editing
  useEffect(() => {
    if (editDataSource) {
      setEditForm({
        name: editDataSource.name,
        description: editDataSource.description || '',
        cacheDurationMinutes: editDataSource.cacheDurationMinutes,
        isEnabled: editDataSource.isEnabled,
      })
    }
  }, [editDataSource])

  const handleCreate = async (data: CreateDataSourceRequest) => {
    const created = await dataSourceService.createDataSource(data)
    setDataSources((prev) => [...prev, created])
  }

  const handleToggle = async (id: string, enabled: boolean) => {
    const updated = await dataSourceService.updateDataSource(id, { isEnabled: enabled })
    setDataSources((prev) =>
      prev.map((ds) => (ds.id === id ? updated : ds))
    )
  }

  const handleTest = async (id: string): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await dataSourceService.testDataSource(id)
      return { success: result.isSuccess, message: result.message }
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : 'Test failed',
      }
    }
  }

  const handleEdit = (dataSource: DataSource) => {
    setEditDataSource(dataSource)
  }

  const handleUpdate = async () => {
    if (!editDataSource) return

    setUpdating(true)
    try {
      const updateData: UpdateDataSourceRequest = {
        name: editForm.name,
        description: editForm.description || undefined,
        cacheDurationMinutes: editForm.cacheDurationMinutes,
        isEnabled: editForm.isEnabled,
      }
      const updated = await dataSourceService.updateDataSource(editDataSource.id, updateData)
      setDataSources((prev) =>
        prev.map((ds) => (ds.id === editDataSource.id ? updated : ds))
      )
      setEditDataSource(null)
    } catch (err) {
      console.error('Failed to update data source:', err)
    } finally {
      setUpdating(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteDataSource) return

    setDeleting(true)
    try {
      await dataSourceService.deleteDataSource(deleteDataSource.id)
      setDataSources((prev) => prev.filter((ds) => ds.id !== deleteDataSource.id))
      setDeleteDataSource(null)
    } catch (err) {
      console.error('Failed to delete data source:', err)
    } finally {
      setDeleting(false)
    }
  }

  const handleRefresh = async (id: string) => {
    const updated = await dataSourceService.refreshDataSource(id)
    setDataSources((prev) =>
      prev.map((ds) => (ds.id === id ? updated : ds))
    )
  }

  const handleRefreshAll = async () => {
    setRefreshing(true)
    try {
      await loadDataSources()
    } finally {
      setRefreshing(false)
    }
  }

  const enabledCount = dataSources.filter((ds) => ds.isEnabled).length
  const totalFieldCount = dataSources.reduce((acc, ds) => acc + ds.fieldCount, 0)

  return (
    <>
      <PageMeta
        title="Data Sources | Admin"
        description="Manage data sources for smart/connected fields"
      />

      <div className="container mx-auto py-8 px-4 max-w-6xl">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                Data Sources
              </h1>
              <p className="text-neutral-500 dark:text-neutral-400 mt-1">
                {tenantId ? `Data sources for ${tenantName || 'selected company'}` : 'Connect external data to your template fields'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Company Filter */}
              <Select value={tenantId || 'all'} onValueChange={setTenantFilter}>
                <SelectTrigger className="w-[200px]">
                  <Building2 className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={handleRefreshAll}
                disabled={loading || refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Source
              </Button>
            </div>
          </div>

          {/* Tenant Filter Banner */}
          {tenantId && (
            <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
              <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="flex items-center justify-between">
                <span>
                  Showing data sources for company:{' '}
                  <Link
                    to={`/admin/companies/${tenantId}`}
                    className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {tenantName || tenantId}
                  </Link>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearTenantFilter}
                  className="h-7 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filter
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Stats */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
              <Database className="h-4 w-4 text-neutral-500" />
              <span className="text-sm text-neutral-600 dark:text-neutral-300">
                <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                  {dataSources.length}
                </span>{' '}
                sources
              </span>
            </div>
            <Badge variant="success" className="py-2">
              {enabledCount} enabled
            </Badge>
            <Badge variant="secondary" className="py-2">
              {totalFieldCount} total fields
            </Badge>
          </div>

          {/* Error State */}
          {error && !loading && (
            <div className="p-4 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
              <Button variant="outline" size="sm" onClick={loadDataSources}>
                Retry
              </Button>
            </div>
          )}

          {/* Data Sources List */}
          <DataSourceList
            dataSources={dataSources}
            loading={loading}
            onToggle={handleToggle}
            onTest={handleTest}
            onEdit={handleEdit}
            onDelete={(ds) => setDeleteDataSource(ds)}
            onRefresh={handleRefresh}
          />
        </div>
      </div>

      {/* Create Dialog */}
      <CreateDataSourceDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreate}
      />

      {/* Edit Dialog */}
      <Dialog open={!!editDataSource} onOpenChange={(open) => !open && setEditDataSource(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Data Source</DialogTitle>
            <DialogDescription>
              Update the configuration for this data source.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                Name
              </label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                Description
              </label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                Cache Duration
              </label>
              <Select
                value={editForm.cacheDurationMinutes.toString()}
                onValueChange={(value) =>
                  setEditForm((prev) => ({ ...prev, cacheDurationMinutes: parseInt(value) }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="360">6 hours</SelectItem>
                  <SelectItem value="1440">24 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDataSource(null)} disabled={updating}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updating || !editForm.name.trim()}>
              {updating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDataSource} onOpenChange={(open) => !open && setDeleteDataSource(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Data Source</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDataSource?.name}"? This action cannot be
              undone, and any fields bound to this source will be unbound.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDataSource(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Fields Dialog */}
      <Dialog open={!!viewFieldsDataSource} onOpenChange={(open) => !open && setViewFieldsDataSource(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>{viewFieldsDataSource?.name} - Fields</DialogTitle>
                <DialogDescription>
                  {viewFieldsDataSource?.fieldCount} fields available for binding
                </DialogDescription>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setViewFieldsDataSource(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <DataSourceFieldsPanel
            fields={fields}
            loading={fieldsLoading}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
